<div class="markup-editor-overlay" data-testid="markup-editor-overlay">
  <!-- Header row -->
  <div class="markup-header" data-testid="markup-header" [attr.inert]="conflict ? '' : null">
    <div class="markup-toolbar" data-testid="markup-toolbar">
      <input class="filename-input"
             data-testid="markup-filename-input"
             type="text"
             placeholder="Filename here..."
             [value]="config?.filename || ''"
             [title]="config?.filename || ''"
             (input)="onFilenameChange($event)">

      <span class="toolbar-divider"></span>

      @if (showKindChoice) {
        <!-- Field note edits the page's layer; As-built records a new deviation on every save (spec §15) -->
        <!-- A mode, not navigation: the drawings segmented pill (list toolbar view-toggle), as on mobile -->
        <div class="pill-toggle" role="group" aria-label="Redline type" data-testid="markup-kind-toggle"
             [matTooltip]="kindLockCopy" [matTooltipDisabled]="canSwitchKind">
          <button type="button"
                  data-testid="markup-kind-note"
                  [class.active]="redlineKind === 'note'"
                  [attr.aria-pressed]="redlineKind === 'note'"
                  [disabled]="redlineKind !== 'note' && (!canSwitchKind || isSaving)"
                  (click)="switchKind('note')">Field note</button>
          <button type="button"
                  data-testid="markup-kind-as-built"
                  [class.active]="redlineKind === 'as_built'"
                  [attr.aria-pressed]="redlineKind === 'as_built'"
                  [disabled]="redlineKind !== 'as_built' && (!canSwitchKind || isSaving)"
                  (click)="switchKind('as_built')">As-built</button>
        </div>

        <span class="toolbar-divider"></span>
      }

      @if (showVisibilityToggle) {
        <!-- Which row is edited: the page's shared redline or the user's private one (a record's redline is always shared) -->
        <div class="pill-toggle" role="group" aria-label="Redline" data-testid="markup-visibility-toggle"
             matTooltip="Save or undo your changes first" [matTooltipDisabled]="!hasUnsavedRedline">
          <button type="button"
                  data-testid="markup-visibility-shared"
                  [class.active]="redlineVisibility === 'shared'"
                  [attr.aria-pressed]="redlineVisibility === 'shared'"
                  [disabled]="redlineVisibility !== 'shared' && (hasUnsavedRedline || isSaving)"
                  (click)="switchVisibility('shared')">Shared</button>
          <button type="button"
                  data-testid="markup-visibility-private"
                  [class.active]="redlineVisibility === 'private'"
                  [attr.aria-pressed]="redlineVisibility === 'private'"
                  [disabled]="redlineVisibility !== 'private' && (hasUnsavedRedline || isSaving)"
                  (click)="switchVisibility('private')">Private</button>
        </div>

        <span class="toolbar-divider"></span>
      }

      <!-- Drawing tools. W59: a shared row someone else owns is view-only (the API answers MARKUP_NOT_OWNER) -->
      <span class="tool-group" data-testid="markup-tools" [matTooltip]="lockedCopy" [matTooltipDisabled]="!rowLocked">
      <!-- W76: Select (the arrow): no drawing tool, so shapes can be picked and deleted; the state Esc gives -->
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-select"
              [class.active]="activeTool === null"
              (click)="selectMode()"
              aria-label="Select (Esc)"
              matTooltip="Select (Esc)">
        <mat-icon class="select-icon">near_me</mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-freehand"
              [class.active]="activeTool === MarkupTool.FREEHAND"
              (click)="selectTool(MarkupTool.FREEHAND)"
              title="Freehand Draw">
        <mat-icon>fiber_manual_record</mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-line"
              [class.active]="activeTool === MarkupTool.LINE"
              (click)="selectTool(MarkupTool.LINE)"
              title="Line">
        <mat-icon svgIcon="markup-line"></mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-rectangle"
              [class.active]="activeTool === MarkupTool.RECTANGLE"
              (click)="selectTool(MarkupTool.RECTANGLE)"
              title="Rectangle">
        <mat-icon>crop_square</mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-circle"
              [class.active]="activeTool === MarkupTool.CIRCLE"
              (click)="selectTool(MarkupTool.CIRCLE)"
              title="Circle">
        <mat-icon>panorama_fish_eye</mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-cloud"
              [class.active]="activeTool === MarkupTool.CLOUD"
              (click)="selectTool(MarkupTool.CLOUD)"
              title="Cloud">
        <mat-icon>cloud_queue</mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-polygon"
              [class.active]="activeTool === MarkupTool.POLYGON"
              (click)="selectTool(MarkupTool.POLYGON)"
              title="Polygon">
        <mat-icon svgIcon="markup-shape"></mat-icon>
      </button>
      <button class="tool-btn"
              [disabled]="rowLocked"
              data-testid="markup-tool-text"
              [class.active]="activeTool === MarkupTool.TEXT"
              (click)="selectTool(MarkupTool.TEXT)"
              title="Text">
        <mat-icon>title</mat-icon>
      </button>
      </span>

      <!-- Context-sensitive size controls (visible when a tool is active) -->
      @if (activeTool) {
        <span class="toolbar-divider"></span>
        <div class="size-controls" data-testid="markup-size-controls">
          @for (size of sizes; track size) {
            <button class="size-btn"
                    [class.active]="activeSize === size"
                    (click)="setSize(size)"
                    [attr.data-testid]="'markup-size-' + size">
              {{ size }}
            </button>
          }
          @if (activeTool === MarkupTool.LINE) {
            <button class="size-btn arrow-toggle"
                    [class.active]="lineArrowMode"
                    (click)="toggleLineArrow()"
                    data-testid="markup-arrow-toggle"
                    title="Arrow endpoint">
              <mat-icon>arrow_forward</mat-icon>
            </button>
          }
        </div>
      }

      <span class="toolbar-divider"></span>

      <!-- Color palette -->
      <div class="color-palette" data-testid="markup-color-palette">
        @for (color of colorPalette; track color) {
          <button class="color-swatch"
                  [attr.data-testid]="'markup-color-' + color"
                  [class.active]="activeColor === color"
                  [style.background-color]="color"
                  (click)="setColor(color)"
                  [title]="color">
          </button>
        }
      </div>

      <span class="toolbar-divider"></span>

      <!-- Undo / Redo -->
      <button class="tool-btn"
              data-testid="markup-undo"
              [disabled]="!canUndo"
              (click)="undo()"
              title="Undo (Ctrl+Z)">
        <mat-icon>undo</mat-icon>
      </button>
      <button class="tool-btn"
              data-testid="markup-redo"
              [disabled]="!canRedo"
              (click)="redo()"
              title="Redo (Ctrl+Y)">
        <mat-icon>redo</mat-icon>
      </button>
      <!-- W76: deletes the selected shape(s), as the Delete / Backspace keys do -->
      <button class="tool-btn"
              data-testid="markup-delete"
              [disabled]="!canDelete"
              (click)="deleteSelected()"
              aria-label="Delete (Del)"
              matTooltip="Delete (Del)">
        <mat-icon svgIcon="trash"></mat-icon>
      </button>
    </div>

    <span class="tool-group" [matTooltip]="lockedCopy" [matTooltipDisabled]="!rowLocked">
      <button class="save-btn"
              data-testid="markup-save-btn"
              [disabled]="!canSave"
              (click)="saveAll()">
        @if (isSaving) {
          <mat-spinner diameter="18"></mat-spinner>
        }
        <!-- W62: grey until something changes; "Saved ✓" for a moment after a save -->
        {{ isSaving ? 'Saving...' : savedFlash ? 'Saved ✓' : 'Save' }}
      </button>
    </span>

    <button class="close-btn" data-testid="markup-close-btn" (click)="close()" title="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  @if (asBuiltSource) {
    <!-- 7.2 (P17): a reviewed mark never changes; the record's edit records a new one -->
    <div class="mode-note" role="status" data-testid="markup-as-built-reviewed-note">
      This as-built mark is {{ (asBuiltSource.review_status || 'locked') === 'open' ? 'someone else\'s' : asBuiltSource.review_status }}, so it stays as it is. Saving records your changes as a new as-built mark for review.
    </div>
  } @else if (asBuiltCopied) {
    <div class="mode-note" role="status" data-testid="markup-as-built-copied-note">Saved as a new as-built mark. The earlier one stays on the sheet.</div>
  }
  @if (recordsAsBuilt) {
    <div class="mode-note" role="status" data-testid="markup-as-built-note">
      @if (asBuiltRecorded && nothingToRecord) {
        As-built mark recorded. Draw the next one, or close.
      } @else {
        Each save records one as-built mark.
      }
    </div>
  }

  <!-- Content area -->
  <div class="markup-content" data-testid="markup-content" [attr.inert]="conflict ? '' : null">
    <!-- Page sidebar (PDFs only) -->
    @if (showSidebar) {
      <div class="page-sidebar" data-testid="markup-page-sidebar">
        @for (thumb of pageThumbnails; track $index; let i = $index) {
          <div class="page-thumb"
               [attr.data-testid]="'markup-page-thumb-' + i"
               [class.active]="activePageIndex === i"
               (click)="scrollToPage(i)">
            <div class="thumb-image">
              @if (thumb) {
                <img [src]="thumb" [alt]="'Page ' + (i + 1)">
              } @else {
                <div class="thumb-placeholder"></div>
              }
            </div>
            <span class="page-number">{{ i + 1 }}</span>
          </div>
        }
      </div>
    }

    <!-- Canvas area -->
    @if (config?.isDocument) {
      <!-- Multi-page scrollable container for PDFs -->
      <div class="canvas-scroll-container" data-testid="markup-scroll-container" tabindex="-1" #scrollContainer>
        @for (pageCanvas of pageCanvases; track $index; let i = $index) {
          <div class="page-canvas-wrapper"
               [attr.data-testid]="'markup-page-canvas-' + i"
               [class.active-page]="activePageIndex === i"
               [attr.data-page]="i"
               (click)="setActivePage(i)">
            @if (!pageCanvas.rendered) {
              <div class="page-placeholder"
                   [style.width.px]="pageCanvas.width"
                   [style.height.px]="pageCanvas.height">
                @if (pageCanvas.loading) {
                  <mat-spinner diameter="32"></mat-spinner>
                }
              </div>
            }
            <canvas [id]="'page-canvas-' + i"></canvas>
            @if (isSheetRedline && pageCanvas.rendered) {
              <!-- Fine redlining: the page-sized layers follow the canvas's zoom and pan -->
              <div class="zoom-layer" data-testid="markup-zoom-layer" [style.transform]="overlayTransform">
              @for (layer of redlineLayers; track layer.id) {
                <!-- W76: context only (not this row): faded, never selectable -->
                <img class="redline-layer" data-testid="markup-redline-layer" [src]="layer.preview_url" alt="" decoding="async">
              }
              @for (layer of redlineLayers; track layer.id) {
                @if (contextBox(layer); as box) {
                  <!-- Review M1: the keyboard's way to a faded row: Tab focuses it (its card shows), Enter edits an editable note.
                       Pointer events pass through to the canvas, so drawing over it is unaffected. -->
                  <button type="button" class="context-hotspot" data-testid="markup-context-hotspot"
                          [style.left.%]="box.x0 * 100" [style.top.%]="box.y0 * 100"
                          [style.width.%]="(box.x1 - box.x0) * 100" [style.height.%]="(box.y1 - box.y0) * 100"
                          [attr.aria-label]="contextCopy(layer) + (canEditContext(layer) ? '. Press Enter to edit it.' : '')"
                          (focus)="focusContext(layer)" (blur)="releaseContextCard()"
                          (keydown.enter)="canEditContext(layer) && editSheetRedline(layer)"></button>
                }
              }
              </div>
              @if (hoveredLayer; as hovered) {
                <!-- W76: what that faded mark is, in the white tooltip's style; the sheet's own field note can be edited from here -->
                <div class="context-card" data-testid="markup-context-card" role="group" [attr.aria-label]="contextCopy(hovered.row)"
                     [class.context-card--left]="hovered.box.x1 > 0.7"
                     [style.left.%]="overlayAt(hovered.box.x1 > 0.7 ? hovered.box.x0 : hovered.box.x1, hovered.box.y0).left"
                     [style.top.%]="overlayAt(hovered.box.x0, hovered.box.y0).top"
                     (mouseenter)="keepContextCard()" (mouseleave)="releaseContextCard()" (focusin)="keepContextCard()" (focusout)="releaseContextCard()">
                  <span data-testid="markup-context-copy">{{ contextCopy(hovered.row) }}</span>
                  @if (canEditContext(hovered.row)) {
                    <button type="button" class="context-card-action" data-testid="markup-context-edit" (click)="editSheetRedline(hovered.row)">Edit sheet redline</button>
                  }
                </div>
              }
            }
            @if (config?.contextPin && pageCanvas.rendered) {
              <!-- W51: the record's point, for context only: an image over the page, not a canvas object, so never saved -->
              <img class="context-pin" data-testid="markup-context-pin" src="/assets/icons/map/markers/location-pin-filled.svg" alt="" aria-hidden="true"
                   [style.left.%]="overlayAt(config.contextPin.x, config.contextPin.y).left" [style.top.%]="overlayAt(config.contextPin.x, config.contextPin.y).top">
            }
          </div>
        }
      </div>
      @if (isSheetRedline && pageCanvases[0]?.rendered) {
        <!-- Fine redlining: the sheet viewers' zoom stack, with Fit back to the whole page -->
        <app-sheet-zoom-controls testIdPrefix="markup" [zoom]="zoom" [canZoomIn]="canZoomIn" [canZoomOut]="canZoomOut"
          [showFit]="true" [canFit]="canZoomOut" (zoomIn)="zoomIn()" (zoomOut)="zoomOut()" (fit)="fitView()" />
      }
    } @else {
      <!-- Single canvas for images -->
      <div class="canvas-container" data-testid="markup-canvas-container"
           [style.--blur-src]="'url(' + (config?.imageUrl || '') + ')'">
        @if (isLoadingPage) {
          <mat-spinner diameter="32"></mat-spinner>
        }
        <canvas #markupCanvas data-testid="markup-canvas"></canvas>
      </div>
    }
  </div>

  @if (conflict) {
    <div class="conflict-backdrop" data-testid="markup-conflict">
      <div class="conflict-panel" role="alertdialog" aria-modal="true" aria-labelledby="markup-conflict-title"
           aria-describedby="markup-conflict-text" (keydown)="onConflictKeydown($event)">
        <h2 id="markup-conflict-title" class="visually-hidden">Redline conflict</h2>
        <p id="markup-conflict-text" class="conflict-text">Someone else changed this redline. Reload to see their version.</p>
        <div class="conflict-actions">
          <button type="button" class="conflict-reload-btn" data-testid="markup-conflict-reload" #conflictReload
                  (click)="resolveConflict('reload')">Reload</button>
          <button type="button" class="save-btn" data-testid="markup-conflict-keep" #conflictKeep
                  (click)="resolveConflict('keep')">Keep mine</button>
        </div>
      </div>
    </div>
  }

  @if (saveError) {
    <span class="save-error" role="alert" data-testid="markup-save-error">
      {{ saveError }}
      @if (canRetryPreview) {
        <button type="button" class="save-error-retry" data-testid="markup-retry-preview" [disabled]="isSaving" (click)="retryPreview()">Retry</button>
      }
    </span>
  }
</div>
